import React, { useEffect, useState } from "react";
import client, { apiErr } from "../lib/api";
import { Button, Card, Badge } from "../components/ui";
import { PUBLIC_LANGS } from "../i18n";
import { Star, Check, X, MessageSquare, Loader2, BadgeCheck } from "lucide-react";

const LANG_LABEL = PUBLIC_LANGS.reduce((m, l) => { m[l.code] = l.native; return m; }, { ENG: "English" });

function Stars({ value }) {
  return (
    <span className="inline-flex items-center gap-0.5">
      {Array.from({ length: 7 }, (_, i) => i + 1).map((n) => (
        <Star key={n} size={13} className={n <= value ? "fill-amber-400 text-amber-400" : "text-black/25 dark:text-white/25"} />
      ))}
    </span>
  );
}

export default function ReviewsAdmin() {
  const [tab, setTab] = useState("pending");
  const [data, setData] = useState({ items: [], counts: {}, programs: {} });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [msg, setMsg] = useState("");

  const load = (status = tab) => {
    setLoading(true);
    client.get("/admin/reviews", { params: { status } })
      .then((r) => setData(r.data)).catch((e) => setMsg(apiErr(e))).finally(() => setLoading(false));
  };
  useEffect(() => { load(tab); /* eslint-disable-next-line */ }, [tab]);

  const moderate = async (id, action) => {
    setBusy(id);
    try {
      await client.post(`/admin/reviews/${id}/moderate`, { action });
      setMsg(action === "approve" ? "Review approved and now visible on the Solutions page." : "Review rejected.");
      load(tab);
    } catch (e) { setMsg(apiErr(e)); }
    setBusy("");
  };

  const TABS = [["pending", "Pending"], ["approved", "Approved"], ["rejected", "Rejected"], ["all", "All"]];

  return (
    <div className="space-y-5" data-testid="reviews-admin">
      <div className="flex items-center gap-3">
        <MessageSquare size={22} className="text-[var(--primary)]" />
        <h1 className="font-head text-2xl">Ratings &amp; Reviews</h1>
      </div>
      <p className="text-sm opacity-70">Customer reviews from the public Solutions page. Reviews stay hidden until you approve them.</p>

      <div className="flex flex-wrap gap-2">
        {TABS.map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`reviews-tab-${k}`}
            className={`rounded-full px-3 py-1 text-sm ${tab === k ? "bg-[var(--primary)] text-white" : "border border-black/15 dark:border-white/15 hover:bg-black/5 dark:hover:bg-white/10"}`}>
            {label}{data.counts[k] != null ? ` (${data.counts[k]})` : ""}
          </button>
        ))}
      </div>

      {msg && <Card className="border-[var(--primary)]/40 text-sm" data-testid="reviews-admin-msg">{msg}</Card>}
      {loading && <Card className="opacity-60">Loading…</Card>}
      {!loading && data.items.length === 0 && <Card className="text-center opacity-60">No {tab === "all" ? "" : tab} reviews.</Card>}

      <div className="space-y-3">
        {data.items.map((r) => (
          <Card key={r.id} className="space-y-2" data-testid={`review-card-${r.id}`}>
            <div className="flex flex-wrap items-center gap-3">
              <Stars value={r.rating} />
              <span className="text-sm font-semibold text-[var(--primary)]">{r.program_label || (data.programs || {})[r.program] || r.program}</span>
              <Badge color="info">{LANG_LABEL[r.lang] || r.lang}</Badge>
              <Badge color={r.status === "approved" ? "success" : r.status === "rejected" ? "danger" : "warning"}>{r.status}</Badge>
              {r.verified && <Badge color="success" data-testid={`admin-verified-${r.id}`}><BadgeCheck size={11} className="inline" /> Verified customer</Badge>}
              <span className="text-xs opacity-50">{(r.created_at || "").slice(0, 10)} · {r.name || "Anonymous"}{r.email ? ` · ${r.email}` : ""}</span>
            </div>
            <div className="text-sm opacity-90">{r.comment}</div>
            {r.comment_en && r.lang !== "ENG" && <div className="border-t border-black/5 pt-1 text-xs italic opacity-70 dark:border-white/5">EN (customer-provided): {r.comment_en}</div>}
            <div className="flex gap-2">
              {r.status !== "approved" && (
                <Button variant="subtle" onClick={() => moderate(r.id, "approve")} disabled={busy === r.id} data-testid={`review-approve-${r.id}`}>
                  {busy === r.id ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />} Approve
                </Button>
              )}
              {r.status !== "rejected" && (
                <Button variant="ghost" onClick={() => moderate(r.id, "reject")} disabled={busy === r.id} data-testid={`review-reject-${r.id}`}>
                  <X size={15} /> Reject
                </Button>
              )}
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
